import { useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import { format, parseISO } from 'date-fns';
import { CondensedPotSummary } from '../CondensedPotSummary';
import { useApprovePot } from '../../hooks/useApprovePot';
import {
  useResolveRejectionReason,
  useRemoveRejectionReason,
} from '../../hooks/useRejectionReasons';
import type { PotDetailsDisplayModel } from '../../types/potDetails.types';

interface PotApprovalPanelContentProps {
  potDetails: PotDetailsDisplayModel;
  onSuccess: () => void;
  onReject?: () => void;
}

export function PotApprovalPanelContent({
  potDetails,
  onSuccess,
  onReject,
}: PotApprovalPanelContentProps) {
  const { mutate: approvePot, isPending: isApproving } = useApprovePot();

  const { mutate: resolveReason, isPending: isResolving } =
    useResolveRejectionReason();

  const { mutate: removeReason, isPending: isRemoving } =
    useRemoveRejectionReason();

  const isPending = isApproving || isResolving || isRemoving;

  const handleConfirmApproval = () => {
    approvePot(
      {
        potPeriodId: potDetails.potPeriodId,
        potId: potDetails.id,
      },
      {
        onSuccess: () => onSuccess(),
      }
    );
  };

  const handleResolveReason = useCallback(
    (reasonId: string) => {
      resolveReason({ potId: potDetails.id, reasonId });
    },
    [resolveReason, potDetails.id]
  );

  const handleRemoveReason = useCallback(
    (reasonId: string) => {
      removeReason({ potId: potDetails.id, reasonId });
    },
    [removeReason, potDetails.id]
  );

  // Get rejection reasons by status
  const unresolvedRejections = potDetails.rejectionReasons.filter(
    r => !r.isResolved
  );
  const resolvedRejections = potDetails.rejectionReasons.filter(
    r => r.isResolved
  );
  const hasOutstandingRejections =
    potDetails.approvals.hasOutstandingRejections;

  const formatDateTime = (dateString: string) => {
    return format(parseISO(dateString), 'dd MMM yyyy, HH:mm');
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Stack spacing={3}>
          {/* Rejection Reasons Card - show if there are unresolved rejections */}
          {unresolvedRejections.length > 0 && (
            <Card variant="outlined" sx={{ borderColor: 'error.main' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="error.main">
                  Outstanding Rejection Reasons ({unresolvedRejections.length})
                </Typography>
                <List dense>
                  {unresolvedRejections.map(reason => (
                    <ListItem key={reason.id}>
                      <ListItemText
                        primary={reason.reason}
                        secondary={`Added by ${reason.createdByName}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <ListItemSecondaryAction>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Mark as resolved">
                            <IconButton
                              edge="end"
                              size="small"
                              color="success"
                              onClick={() => handleResolveReason(reason.id)}
                              disabled={isPending}
                              data-testid="approval-resolve-reason-button"
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton
                              edge="end"
                              size="small"
                              color="error"
                              onClick={() => handleRemoveReason(reason.id)}
                              disabled={isPending}
                              data-testid="approval-remove-reason-button"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}

          {/* Resolved Reasons Card */}
          {resolvedRejections.length > 0 && (
            <Card variant="outlined" sx={{ borderColor: 'success.main' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="text.secondary">
                  Resolved Reasons ({resolvedRejections.length})
                </Typography>
                <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1 }}>
                  {resolvedRejections.map(reason => (
                    <ListItem
                      key={reason.id}
                      sx={{ textDecoration: 'line-through', opacity: 0.7 }}
                    >
                      <ListItemText
                        primary={reason.reason}
                        secondary={
                          <Stack component="span" spacing={0.5}>
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                            >
                              Added by {reason.createdByName}
                            </Typography>
                            {reason.resolvedAt && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="success.main"
                              >
                                Resolved by {reason.resolvedByName} on{' '}
                                {formatDateTime(reason.resolvedAt)}
                              </Typography>
                            )}
                          </Stack>
                        }
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: { textDecoration: 'line-through' },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}

          {/* Condensed Pot Summary */}
          <CondensedPotSummary potDetails={potDetails} showDeductions={true} />
        </Stack>
      </Box>

      {/* Fixed bottom action area */}
      <Box
        sx={{
          p: 3,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        {/* Helper text */}
        {hasOutstandingRejections ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Resolve or remove all outstanding rejection reasons before
            approving.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Approving this pot will send it for payment. This action cannot be
            undone.
          </Alert>
        )}

        <Stack spacing={1.5}>
          <Button
            variant="contained"
            color="success"
            size="large"
            fullWidth
            startIcon={
              isApproving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CheckCircleIcon />
              )
            }
            onClick={handleConfirmApproval}
            disabled={isPending || hasOutstandingRejections}
            sx={{
              '&.Mui-disabled': {
                backgroundColor: 'action.disabledBackground',
                color: 'action.disabled',
              },
            }}
            data-testid="confirm-approval-button"
          >
            {isApproving ? 'Approving...' : 'Confirm Approval'}
          </Button>

          {onReject && (
            <Button
              variant="outlined"
              color="error"
              size="large"
              fullWidth
              startIcon={<CancelIcon />}
              onClick={onReject}
              disabled={isPending}
              data-testid="reject-pot-button"
            >
              Reject Pot
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
